import React, { useMemo, useState } from "react";
import { TL } from "../i18n";
import { ReactFlow, Background, Controls, MiniMap, Handle, Position, Panel } from "@xyflow/react";
import "@xyflow/react/dist/style.css";

// Read a person's spouse list (multi-spouse aware, tolerant of the legacy single spouse_id).
const spouseIdsOf = (p, byId) => {
  if (!p) return [];
  if (Array.isArray(p.spouse_ids) && p.spouse_ids.length) return p.spouse_ids.filter((x) => byId[x]);
  return p.spouse_id && byId[p.spouse_id] ? [p.spouse_id] : [];
};
// A person's partners (current + former spouses) — used to decide if two parents form a couple.
const partnersOf = (p, byId) => {
  if (!p) return [];
  const cur = spouseIdsOf(p, byId);
  const fmr = Array.isArray(p.former_spouse_ids) ? p.former_spouse_ids.filter((x) => byId[x]) : [];
  return [...new Set([...cur, ...fmr])];
};
const areCouple = (a, b, byId) => !!(a && b && byId[a] && byId[b] && (partnersOf(byId[a], byId).includes(b) || partnersOf(byId[b], byId).includes(a)));
// Which parent line(s) a child descends from (name-based per-child choice).
const descParentsOf = (c) => {
  const df = c.descend_from || "both";
  if (df === "parent") return [c.parent_id];
  if (df === "parent2") return [c.parent_id2];
  return [c.parent_id, c.parent_id2];
};
// Pick readable text color for an arbitrary box fill.
const darkText = (hex) => {
  if (!hex) return false;
  const h = hex.replace("#", "");
  if (h.length < 6) return false;
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62;
};

function PersonNode({ data }) {
  const landscape = data.orientation === "landscape";
  const childT = landscape ? Position.Left : Position.Top;
  const childS = landscape ? Position.Right : Position.Bottom;
  const spouseT = landscape ? Position.Top : Position.Left;
  const spouseS = landscape ? Position.Bottom : Position.Right;
  const fg = darkText(data.color) ? "#0a1a3a" : "#ffffff";
  return (
    <div
      data-testid={`node-${data.id}`}
      style={{
        width: 190,
        minHeight: data.minH || undefined,
        background: data.color || "#0F62FE", color: fg,
        outline: data.selected ? "3px solid var(--primary)" : (data.highlighted ? "3px solid #0a0a0a" : "none"),
        outlineOffset: 2,
        opacity: data.dim ? 0.2 : 1,
        transition: "opacity 180ms ease",
      }}
      className={`relative overflow-hidden rounded-md px-3.5 py-2.5 shadow-md${data.selected ? " select-flash" : ""}`}
    >
      <Handle id="ct" type="target" position={childT} style={{ opacity: 0 }} />
      <Handle id="cs" type="source" position={childS} style={{ opacity: 0 }} />
      <Handle id="st" type="target" position={spouseT} style={{ opacity: 0, ...(landscape ? { left: 28 } : { top: 24 }) }} />
      <Handle id="ss" type="source" position={spouseS} style={{ opacity: 0, ...(landscape ? { left: 28 } : { top: 24 }) }} />
      <button
        className="nodrag absolute right-1.5 top-1.5 opacity-80 hover:opacity-100"
        data-testid={`node-select-${data.id}`}
        onClick={(e) => { e.stopPropagation(); data.onToggle?.(data.id); }}
      >
        {data.selected
          ? <span className="flex h-4 w-4 items-center justify-center rounded-sm bg-white/95 text-[10px] font-bold leading-none text-[var(--primary)]">✓</span>
          : <span className="block h-4 w-4 rounded-sm border-2" style={{ borderColor: fg, opacity: 0.5 }} />}
      </button>
      <button
        className="nodrag absolute right-7 top-1.5 opacity-80 hover:opacity-100"
        data-testid={`node-focus-${data.id}`}
        title={TL("Focus this bloodline (ancestors + descendants)")}
        onClick={(e) => { e.stopPropagation(); data.onFocus?.(data.isFocusRoot ? null : data.id); }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={data.isFocusRoot ? "#ffd166" : fg} strokeWidth="2.2" strokeLinecap="round">
          <circle cx="12" cy="12" r="6.5" /><line x1="12" y1="1" x2="12" y2="4" /><line x1="12" y1="20" x2="12" y2="23" /><line x1="1" y1="12" x2="4" y2="12" /><line x1="20" y1="12" x2="23" y2="12" />
        </svg>
      </button>
      <div className={data.photo ? "pt-4" : "pe-12"}>
        {data.photo && (
          <div className="mx-auto mb-1.5 h-11 w-11 overflow-hidden rounded-full border-2 border-white/70">
            <img src={data.photo} alt="" draggable={false}
              style={{ width: "100%", height: "100%", objectFit: "cover", transform: `rotate(${data.photo_rot || 0}deg)` }} />
          </div>
        )}
        <div className={`font-head text-sm font-bold uppercase leading-tight tracking-wide ${data.photo ? "text-center" : ""}`} style={{ overflowWrap: "break-word", wordBreak: "normal", hyphens: "none" }}>{data.name || "Unnamed"}</div>
        {data.role && <div className={`truncate text-[11px] font-medium opacity-90 ${data.photo ? "text-center" : ""}`}>{data.role}</div>}
        {(data.dob || data.dod) && (
          <div className={`truncate text-[10px] opacity-75 ${data.photo ? "text-center" : ""}`}>
            {data.dob ? `b.${data.dob}` : ""}{data.dob && data.dod ? " · " : ""}{data.dod ? `d.${data.dod}` : ""}
          </div>
        )}
        {data.spouse && <div className={`truncate text-[10px] opacity-90 ${data.photo ? "text-center" : ""}`}>⚭ {data.spouse}</div>}
        {data.former && <div className={`truncate text-[10px] opacity-70 ${data.photo ? "text-center" : ""}`}>⚭ {data.former} (former)</div>}
      </div>
    </div>
  );
}

// Invisible junction that sits on a couple's marriage line; children of the couple hang from it
// via a single shared drop-line (standard genogram look) instead of one line per parent.
function UnionNode({ data }) {
  const landscape = data.orientation === "landscape";
  const src = landscape ? Position.Right : Position.Bottom;
  return (
    <div style={{ width: 1, height: 1, opacity: 0, pointerEvents: "none" }}>
      <Handle id="u" type="source" position={src} style={{ opacity: 0 }} />
    </div>
  );
}

const nodeTypes = { person: PersonNode, union: UnionNode };

export default function FamilyDiagram({ people, descendants, focus, showMaiden, onConnectParent, onMove, orientation = "portrait", selectedIds, onToggleSelect, rtl = false }) {
  const landscape = orientation === "landscape";
  const selSet = selectedIds || new Set();
  const [focusBranch, setFocusBranch] = useState(null);
  const [focusPerson, setFocusPerson] = useState(null);
  const byId = useMemo(() => Object.fromEntries(people.map((p) => [p.id, p])), [people]);

  // Bloodline of the focused person: their direct ancestors + descendants (+ spouses so couples stay lit).
  const lineageSet = useMemo(() => {
    if (!focusPerson || !byId[focusPerson]) return null;
    const set = new Set();
    const up = [focusPerson];
    while (up.length) { const id = up.pop(); if (set.has(id)) continue; set.add(id); const p = byId[id]; [p?.parent_id, p?.parent_id2].forEach((pp) => { if (pp && byId[pp] && !set.has(pp)) up.push(pp); }); }
    const down = [focusPerson]; const dseen = new Set();
    while (down.length) { const id = down.pop(); if (dseen.has(id)) continue; dseen.add(id); set.add(id); people.forEach((c) => { if ((c.parent_id === id || c.parent_id2 === id) && !dseen.has(c.id)) down.push(c.id); }); }
    [...set].forEach((id) => { const sp = byId[id]; (sp?.spouse_ids || (sp?.spouse_id ? [sp.spouse_id] : [])).forEach((s) => { if (byId[s]) set.add(s); }); });
    return set;
  }, [focusPerson, people, byId]);

  // ---- Household layout: married partners SIDE-BY-SIDE on one row, children below via a shared bus ----
  const layout = useMemo(() => {
    const dCache = {};
    const ownDepth = (id, g = 0) => {
      if (dCache[id] != null) return dCache[id];
      const p = byId[id];
      if (!p || g > 80) return 0;
      const ps = [p.parent_id, p.parent_id2].filter((x) => x && byId[x]);
      const d = ps.length ? Math.min(...ps.map((x) => ownDepth(x, g + 1))) + 1 : 0;
      dCache[id] = d; return d;
    };
    const groupId = {}, groups = {}, seen = new Set();
    people.forEach((p) => {
      if (seen.has(p.id)) return;
      const comp = [], stack = [p.id], local = new Set();
      while (stack.length) {
        const id = stack.pop();
        if (local.has(id)) continue;
        local.add(id); seen.add(id); comp.push(id);
        spouseIdsOf(byId[id], byId).forEach((s) => { if (byId[s] && !local.has(s)) stack.push(s); });
      }
      const hasPar = (id) => { const q = byId[id]; return [q.parent_id, q.parent_id2].some((x) => x && byId[x]); };
      const head = comp.slice().sort((a, b) =>
        (hasPar(b) ? 1 : 0) - (hasPar(a) ? 1 : 0) ||
        ((byId[b].sibling_ids || []).length ? 1 : 0) - ((byId[a].sibling_ids || []).length ? 1 : 0) ||
        ownDepth(a) - ownDepth(b) ||
        spouseIdsOf(byId[b], byId).length - spouseIdsOf(byId[a], byId).length ||
        (a < b ? -1 : 1))[0];
      // Hub-centred: place the head between its spouses (Greta — Alex — Hetta) for polygamy.
      const headSpouses = spouseIdsOf(byId[head], byId).filter((x) => comp.includes(x));
      const rest = comp.filter((x) => x !== head && !headSpouses.includes(x));
      const half = Math.floor(headSpouses.length / 2);
      const ordered = [...headSpouses.slice(0, half), head, ...headSpouses.slice(half), ...rest];
      ordered.forEach((id) => (groupId[id] = head));
      groups[head] = ordered;
    });
    const childGroupsOf = (H) => {
      const members = new Set(groups[H]);
      const res = new Set();
      people.forEach((c) => {
        const dps = descParentsOf(c).filter((x) => x && byId[x]);
        if (dps.some((pp) => members.has(pp))) { const g = groupId[c.id]; if (g !== H) res.add(g); }
      });
      return [...res];
    };
    // Household generation = bloodline parent's household generation + 1 (married-in roots don't pull descendants up).
    const gGenCache = {};
    const gGen = (H, guard = 0) => {
      if (gGenCache[H] != null) return gGenCache[H];
      if (guard > 120) return 0;
      const members = groups[H];
      const parentGroups = new Set();
      members.forEach((mid) => {
        const p = byId[mid];
        [p.parent_id, p.parent_id2].forEach((pp) => { if (pp && byId[pp] && groupId[pp] !== H) parentGroups.add(groupId[pp]); });
      });
      const g = parentGroups.size ? Math.max(...[...parentGroups].map((pg) => gGen(pg, guard + 1) + 1)) : 0;
      gGenCache[H] = g; return g;
    };
    // Sibling links (work without parents — e.g. patriarch brothers): cluster their households so they stay adjacent on one line.
    const siblingIdsOf = (p) => (Array.isArray(p.sibling_ids) ? p.sibling_ids.filter((x) => byId[x]) : []);
    const sibAdj = {}; Object.keys(groups).forEach((h) => (sibAdj[h] = new Set()));
    people.forEach((p) => siblingIdsOf(p).forEach((sid) => { const a = groupId[p.id], b = groupId[sid]; if (a && b && a !== b) { sibAdj[a].add(b); sibAdj[b].add(a); } }));
    const clusterId = {}, clusterMembers = {}; let ci = 0;
    Object.keys(groups).forEach((h) => {
      if (clusterId[h] != null) return;
      const id = ci++; clusterMembers[id] = []; const st = [h];
      while (st.length) { const x = st.pop(); if (clusterId[x] != null) continue; clusterId[x] = id; clusterMembers[id].push(x); [...(sibAdj[x] || [])].forEach((y) => { if (clusterId[y] == null) st.push(y); }); }
    });
    const clusterGen = {}; Object.keys(clusterMembers).forEach((id) => { clusterGen[id] = Math.max(...clusterMembers[id].map((h) => gGen(h))); });
    const genOf = (H) => clusterGen[clusterId[H]] ?? gGen(H);
    const col = {}, gen = {};
    const placed = new Set();
    const layoutGroup = (H, leftX) => {
      if (placed.has(H)) return 0;
      placed.add(H);
      const members = groups[H];
      const kids = childGroupsOf(H).filter((g) => !placed.has(g)).sort((a, b) => groups[a].length - groups[b].length);
      let childLeft = leftX, childWidth = 0;
      kids.forEach((g) => { const w = layoutGroup(g, childLeft); childLeft += w; childWidth += w; });
      const width = Math.max(members.length, childWidth);
      const center = leftX + width / 2;
      const d = genOf(H);
      // Head first (adjacent to siblings), spouses trailing to the side.
      const start = center - members.length / 2;
      members.forEach((mid, i) => { col[mid] = start + i + 0.5; gen[mid] = d; });
      return width;
    };
    const rootHeads = Object.keys(groups).filter((H) =>
      !groups[H].some((mid) => {
        const p = byId[mid];
        return [p.parent_id, p.parent_id2].some((pp) => pp && byId[pp] && groupId[pp] !== H);
      }));
    const rootSet = new Set(rootHeads);
    let cursor = 0;
    const doneCluster = new Set();
    rootHeads.forEach((H) => {
      const cid = clusterId[H];
      if (doneCluster.has(cid)) return; doneCluster.add(cid);
      const clusterRoots = clusterMembers[cid].filter((h) => rootSet.has(h)).sort((a, b) => groups[a].length - groups[b].length);
      clusterRoots.forEach((h) => { if (placed.has(h)) return; const w = layoutGroup(h, cursor); cursor += Math.max(1, w) + 1; });
    });
    Object.keys(groups).forEach((H) => { if (!placed.has(H)) { const w = layoutGroup(H, cursor); cursor += Math.max(1, w) + 1; } });
    return { col, gen, groups, groupId };
  }, [people, byId]);

  // Couples whose children descend from BOTH parents → one shared union junction per couple.
  const unions = useMemo(() => {
    const m = {};
    people.forEach((c) => {
      if ((c.descend_from || "both") !== "both") return;
      const a = c.parent_id, b = c.parent_id2;
      if (!(a && b && byId[a] && byId[b] && areCouple(a, b, byId))) return;
      const key = [a, b].sort().join("|");
      if (!m[key]) m[key] = { a, b, children: [] };
      m[key].children.push(c.id);
    });
    return m;
  }, [people, byId]);

  const nodes = useMemo(() => {
    const { col, gen, groups, groupId } = layout;
    const CELL = 240, GEN = 180;
    const short = (q) => [q?.first, q?.last].filter(Boolean).join(" ") || "Unnamed";
    const label = (ids) => { const arr = (Array.isArray(ids) ? ids : []).map((id) => byId[id]).filter(Boolean); return arr.length ? short(arr[0]) + (arr.length > 1 ? ` +${arr.length - 1}` : "") : ""; };
    const contentLines = (p) => {
      const nm = [p.first, p.middle, p.last].filter(Boolean).join(" ");
      const nameLines = Math.max(1, Math.ceil(nm.length / 15));
      const sp = (Array.isArray(p.spouse_ids) ? p.spouse_ids : []).some((id) => byId[id]);
      const fm = (Array.isArray(p.former_spouse_ids) ? p.former_spouse_ids : []).some((id) => byId[id]);
      const photoExtra = p.photo ? 5 : 0; // photo circle sits above the name (stacked)
      return nameLines + photoExtra + (p.branch ? 1 : 0) + (p.dob || p.dod ? 1 : 0) + (sp ? 1 : 0) + (fm ? 1 : 0);
    };
    const groupMaxLines = {};
    Object.entries(groups).forEach(([h, members]) => { groupMaxLines[h] = Math.max(...members.map((id) => contentLines(byId[id]))); });
    const personNodes = people.map((p) => {
      const c = col[p.id] ?? 0, g = gen[p.id] ?? 0;
      const cc = rtl ? -c : c;
      let cx, cy;
      if (landscape) { cx = g * (GEN + 60); cy = cc * CELL; }
      else { cx = cc * CELL; cy = g * (GEN + 20); }
      const x = p.x != null ? p.x : cx;
      const y = p.y != null ? p.y : cy;
      const lines = groupMaxLines[groupId[p.id]] ?? contentLines(p);
      return {
        id: p.id, type: "person", position: { x, y },
        data: {
          id: p.id,
          name: [p.first, p.middle, p.last].filter(Boolean).join(" "),
          role: p.branch || "",
          color: p.color, dob: p.dob, dod: p.dod,
          photo: p.photo || "", photo_rot: p.photo_rot || 0, orientation,
          spouse: label(p.spouse_ids),
          former: label(p.former_spouse_ids),
          minH: 22 + lines * 15,
          dim: (!!focusBranch && (p.branch || "") !== focusBranch) || (!!lineageSet && !lineageSet.has(p.id)),
          onFocus: setFocusPerson,
          isFocusRoot: focusPerson === p.id,
          highlighted: descendants.has(p.id) || focus === p.id,
          selected: selSet.has(p.id), onToggle: onToggleSelect,
        },
      };
    });
    // Build invisible union junctions on each couple's marriage line.
    const posById = Object.fromEntries(personNodes.map((n) => [n.id, n.position]));
    const unionNodes = Object.entries(unions).map(([key, u]) => {
      const pa = posById[u.a], pb = posById[u.b];
      if (!pa || !pb) return null;
      let ux, uy;
      if (landscape) { ux = Math.max(pa.x, pb.x) + 191; uy = (pa.y + pb.y) / 2 + 20; }
      else { ux = (pa.x + pb.x) / 2 + 95; uy = Math.min(pa.y, pb.y) + 24; }
      return { id: `u-${key}`, type: "union", position: { x: ux, y: uy }, draggable: false, selectable: false, data: { orientation } };
    }).filter(Boolean);
    return [...personNodes, ...unionNodes];
  }, [people, byId, layout, unions, descendants, focus, landscape, rtl, orientation, selSet, onToggleSelect, focusBranch, lineageSet, focusPerson]);

  const edges = useMemo(() => {
    const { col } = layout;
    const colOf = (id) => (rtl ? -(col[id] ?? 0) : (col[id] ?? 0));
    const out = [];
    // Children of a married couple hang from a shared union junction (single drop-line).
    const unionChildIds = new Set();
    Object.values(unions).forEach((u) => u.children.forEach((id) => unionChildIds.add(id)));
    Object.entries(unions).forEach(([key, u]) => {
      u.children.forEach((cid) => {
        out.push({
          id: `u-${key}-${cid}`, source: `u-${key}`, sourceHandle: "u", target: cid, targetHandle: "ct",
          type: "smoothstep", pathOptions: { borderRadius: 6 },
          animated: descendants.has(cid),
          style: { stroke: byId[u.a]?.color || "#8a8a8a", strokeWidth: 2 },
        });
      });
    });
    // Parent → child (clean right-angle), honoring each child's descend-from choice.
    // Couple-children are drawn above via their union; everyone else gets a direct parent line.
    people.forEach((c) => {
      if (unionChildIds.has(c.id)) return;
      descParentsOf(c).forEach((pid) => {
        if (pid && byId[pid]) out.push({
          id: `${pid}-${c.id}`, source: pid, sourceHandle: "cs", target: c.id, targetHandle: "ct",
          type: "smoothstep", pathOptions: { borderRadius: 6 },
          animated: descendants.has(c.id),
          style: { stroke: byId[pid]?.color || "#8a8a8a", strokeWidth: 2 },
        });
      });
    });
    // Spouse connector: draw from ACTUAL spouse links (hub → each spouse), never chained by adjacency.
    const spSeen = new Set();
    people.forEach((p) => {
      spouseIdsOf(p, byId).forEach((sid) => {
        if (!byId[sid]) return;
        const key = [p.id, sid].sort().join("-");
        if (spSeen.has(key)) return; spSeen.add(key);
        const [l, r] = colOf(p.id) <= colOf(sid) ? [p.id, sid] : [sid, p.id];
        out.push({
          id: `sp-${key}`, source: l, sourceHandle: "ss", target: r, targetHandle: "st",
          type: "smoothstep", pathOptions: { borderRadius: 6 },
          style: { stroke: "#D02670", strokeWidth: 2 }, label: "⚭",
          labelStyle: { fill: "#D02670", fontWeight: 700 }, labelBgStyle: { fill: "#fff", fillOpacity: 0.85 },
        });
      });
    });
    // Sibling connector (grey, dashed): links brothers/sisters — including patriarchs with no parents.
    const spSeenSib = new Set();
    people.forEach((p) => {
      (Array.isArray(p.sibling_ids) ? p.sibling_ids : []).forEach((sid) => {
        if (!byId[sid]) return;
        const key = [p.id, sid].sort().join("-");
        if (spSeenSib.has(key)) return; spSeenSib.add(key);
        const a = (rtl ? -(col[p.id] ?? 0) : (col[p.id] ?? 0));
        const b = (rtl ? -(col[sid] ?? 0) : (col[sid] ?? 0));
        const [l, r] = a <= b ? [p.id, sid] : [sid, p.id];
        out.push({
          id: `sib-${key}`, source: l, sourceHandle: "ss", target: r, targetHandle: "st",
          type: "smoothstep", pathOptions: { borderRadius: 6 },
          style: { stroke: "#8d8d8d", strokeWidth: 1.6, strokeDasharray: "4 4" },
        });
      });
    });
    // Former-spouse connector (faint dashed pink, labelled "former").
    const spSeenFmr = new Set();
    people.forEach((p) => {
      (Array.isArray(p.former_spouse_ids) ? p.former_spouse_ids : []).forEach((sid) => {
        if (!byId[sid]) return;
        const key = [p.id, sid].sort().join("-");
        if (spSeenFmr.has(key)) return; spSeenFmr.add(key);
        const a = (rtl ? -(col[p.id] ?? 0) : (col[p.id] ?? 0));
        const b = (rtl ? -(col[sid] ?? 0) : (col[sid] ?? 0));
        const [l, r] = a <= b ? [p.id, sid] : [sid, p.id];
        out.push({
          id: `fmr-${key}`, source: l, sourceHandle: "ss", target: r, targetHandle: "st",
          type: "smoothstep", pathOptions: { borderRadius: 6 },
          style: { stroke: "#D02670", strokeWidth: 1.4, strokeDasharray: "2 5", opacity: 0.55 },
          label: "⚭ former", labelStyle: { fill: "#D02670", fontSize: 9 }, labelBgStyle: { fill: "#fff", fillOpacity: 0.7 },
        });
      });
    });
    if (!focusBranch && !lineageSet) return out;
    return out.map((e) => {
      const sb = byId[e.source]?.branch || "", tb = byId[e.target]?.branch || "";
      const branchOk = !focusBranch || sb === focusBranch || tb === focusBranch;
      const lineOk = !lineageSet || lineageSet.has(e.source) || lineageSet.has(e.target);
      return (branchOk && lineOk) ? e : { ...e, style: { ...e.style, opacity: 0.07 }, label: undefined };
    });
  }, [people, byId, descendants, layout, unions, rtl, focusBranch, lineageSet]);

  const branches = useMemo(() => {
    const seen = new Map();
    people.forEach((p) => { const b = (p.branch || "").trim(); if (b && !seen.has(b)) seen.set(b, p.color || "#0F62FE"); });
    return [...seen.entries()].map(([name, color]) => ({ name, color }));
  }, [people]);

  return (
    <div style={{ height: 560 }} className="rounded-xl overflow-hidden border border-black/10 dark:border-white/10 bg-white dark:bg-neutral-900" data-testid="family-diagram">
      <ReactFlow
        nodes={nodes} edges={edges} nodeTypes={nodeTypes} fitView
        onConnect={(c) => onConnectParent(c.source, c.target)}
        onNodeClick={(_, n) => { if (n.type === "union") return; onToggleSelect?.(n.id); }}
        onNodeDragStop={(_, n) => { if (n.type === "union") return; onMove(n.id, n.position.x, n.position.y); }}
        proOptions={{ hideAttribution: true }}
      >
        {branches.length > 0 && (
          <Panel position="top-left">
            <div className="max-w-[220px] rounded-lg border border-black/10 bg-white/90 p-2 text-[11px] shadow-sm backdrop-blur dark:border-white/10 dark:bg-neutral-900/90" data-testid="branch-legend">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="font-semibold uppercase tracking-wide opacity-60">{"Branches"}</span>
                {(focusBranch || focusPerson) && (
                  <button onClick={() => { setFocusBranch(null); setFocusPerson(null); }} data-testid="legend-show-all" className="rounded px-1.5 py-0.5 text-[10px] font-medium text-[var(--primary)] hover:bg-black/5 dark:hover:bg-white/10">{TL("Show all")}</button>
                )}
              </div>
              <div className="flex flex-col gap-0.5">
                {branches.map((b) => {
                  const active = focusBranch === b.name;
                  return (
                    <button key={b.name} onClick={() => setFocusBranch(active ? null : b.name)} data-testid={`legend-${b.name}`}
                      className={`flex items-center gap-1.5 rounded px-1 py-0.5 text-start transition-colors ${active ? "bg-black/10 font-semibold dark:bg-white/15" : "opacity-90 hover:bg-black/5 dark:hover:bg-white/10"} ${focusBranch && !active ? "opacity-40" : ""}`}
                      title={TL("Click to focus this branch")}>
                      <span className="inline-block h-3 w-3 shrink-0 rounded-sm" style={{ background: b.color }} />
                      <span className="truncate">{b.name}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-1 text-[9px] opacity-50">{focusBranch ? "Showing one branch" : "Click a branch to focus"}</div>
            </div>
          </Panel>
        )}
        {focusPerson && byId[focusPerson] && (
          <Panel position="top-right">
            <div className="flex items-center gap-2 rounded-lg border border-black/10 bg-white/90 px-2.5 py-1.5 text-[11px] shadow-sm backdrop-blur dark:border-white/10 dark:bg-neutral-900/90" data-testid="focus-person-banner">
              <span className="opacity-70">{TL("Bloodline:")}</span>
              <span className="font-semibold">{[byId[focusPerson].first, byId[focusPerson].last].filter(Boolean).join(" ") || "Unnamed"}</span>
              <button onClick={() => setFocusPerson(null)} data-testid="focus-person-clear" className="rounded px-1.5 py-0.5 font-medium text-[var(--primary)] hover:bg-black/5 dark:hover:bg-white/10">{TL("Clear")}</button>
            </div>
          </Panel>
        )}
        <Background gap={20} color="#c9d3e6" />
        <Controls />
        <MiniMap pannable zoomable nodeColor={(n) => n.data?.color || "#888"} />
      </ReactFlow>
    </div>
  );
}
